import React, { useMemo } from 'react';
import { useLocation } from 'react-router-dom';
import SeoHelmet from './SeoHelmet';
import { detectCatalogSubdomain } from '../features/catalogo/utils/subdomain';

const DEFAULT = {
  title: 'MUNDOXYZ - Tu mundo de posibilidades',
  description: 'La plataforma de negocios digital más completa de Venezuela. Super app con tiendas, delivery, bingo, rifas, economía dual y asistente IA.',
  image: 'https://mundoxyz.com/logo_og.png',
  url: 'https://mundoxyz.com/',
  canonical: 'https://mundoxyz.com/',
  keywords: 'MundoXYZ, super app Venezuela, tiendas online Venezuela, bingo online, rifas Venezuela, delivery Venezuela, Fires, Coins, economía digital, marketplace Venezuela'
};

const ROUTE_META = {
  '/ventas': {
    title: 'MUNDOXYZ - La super app de Venezuela',
    description: 'Descubre la plataforma de negocios digital más completa de Venezuela. Tiendas, delivery, juegos, economía dual y asistente IA en una sola app.',
    keywords: 'MundoXYZ, super app Venezuela, plataforma negocios Venezuela, tiendas online, delivery Venezuela, bingo online, rifas, Fires, Coins, economía digital, asistente IA'
  },
  '/landing': {
    title: 'MUNDOXYZ - La super app de Venezuela',
    description: 'Descubre la plataforma de negocios digital más completa de Venezuela. Tiendas, delivery, juegos, economía dual y asistente IA en una sola app.',
    keywords: 'MundoXYZ, super app Venezuela, plataforma negocios Venezuela, tiendas online, delivery Venezuela, bingo online, rifas, Fires, Coins, economía digital, asistente IA'
  },
  '/tito/info': {
    title: 'Tito - Delivery en Venezuela | MUNDOXYZ',
    description: 'Solicita delivery y transporte en Venezuela con Tito, el servicio integrado de MUNDOXYZ.',
  },
  '/changelog': {
    title: 'Novedades y Actualizaciones | MUNDOXYZ',
    description: 'Descubre las últimas mejoras, funciones y novedades de MUNDOXYZ.',
  },
  '/mercadeo': {
    title: 'Mercadeo y Franquicias | MUNDOXYZ',
    description: 'Oportunidades de negocio, mercadeo y franquicias dentro de la plataforma MUNDOXYZ.',
  },
  '/market': {
    title: 'Mercado MUNDOXYZ - Lugares y Franquicias',
    description: 'Explora lugares, franquicias y servicios disponibles en el mercado de MundoXYZ Venezuela.',
  },
  '/stores': {
    title: 'Directorio de Tiendas — MundoXYZ Venezuela',
    description: 'Descubre tiendas y negocios verificados dentro de MundoXYZ. Productos, servicios, delivery y más en la plataforma digital más completa de Venezuela.',
    keywords: 'tiendas Venezuela, directorio tiendas online, negocios verificados Venezuela, marketplace Venezuela, comprar online Venezuela, MundoXYZ tiendas'
  },
  '/bingo-online': {
    title: 'Bingo Online Venezuela — MundoXYZ',
    description: 'Juega bingo online con miles de usuarios en Venezuela. Salas activas 24/7 con premios en Fires y Coins. Modalidades 75 y 90 bolas.',
    keywords: 'bingo, bingo online Venezuela, bingo en vivo, salas bingo 24/7, cartones bingo, premios bingo, jugar bingo Venezuela, MundoXYZ bingo, bingo gratis Venezuela, bingo en linea'
  },
  '/rifas': {
    title: 'Rifas Online en Venezuela — MundoXYZ',
    description: 'Participa en rifas y sorteos exclusivos de MundoXYZ Venezuela. Compra números con Fires o Coins y reclama premios al instante.',
    keywords: 'rifas online Venezuela, sorteos Venezuela, comprar números rifa, premios rifas, rifas en vivo, MundoXYZ rifas'
  },
  '/transparencia': {
    title: 'Transparencia — Economía MundoXYZ',
    description: 'Consulta métricas públicas de la economía dual de MundoXYZ: circulación de Fires y Coins, usuarios activos, premios distribuidos y más.',
    keywords: 'economía digital Venezuela, Fires Coins, transparencia plataforma, métricas públicas, economía dual, MundoXYZ transparencia'
  },
  '/faq': {
    title: 'Preguntas Frecuentes — MundoXYZ Venezuela',
    description: 'Respuestas a las preguntas más comunes sobre MundoXYZ: tiendas, bingo, rifas, Fires, Coins, delivery, Pepe IA y más.',
    keywords: 'qué es MundoXYZ, cómo jugar bingo Venezuela, cómo participar rifas, Fires Coins, super app Venezuela, FAQ MundoXYZ'
  },
  '/login': {
    title: 'Iniciar Sesión | MUNDOXYZ',
    description: 'Accede a tu cuenta MUNDOXYZ.',
    noindex: true,
  },
  '/registerxyz23': {
    title: 'Registro | MUNDOXYZ',
    description: 'Crea tu cuenta en MUNDOXYZ.',
    noindex: true,
  },
  '/reset-password': {
    title: 'Restablecer Contraseña | MUNDOXYZ',
    description: 'Recupera el acceso a tu cuenta MUNDOXYZ.',
    noindex: true,
  },
};

function matchRouteMeta(pathname) {
  const clean = pathname.replace(/\/$/, '') || '/';
  if (ROUTE_META[clean]) return ROUTE_META[clean];
  if (pathname.startsWith('/raffles/public/')) {
    return {
      title: 'Rifa en MUNDOXYZ',
      description: 'Participa en rifas y sorteos exclusivos de MUNDOXYZ Venezuela.',
    };
  }
  if (pathname.startsWith('/gift-link/')) {
    return {
      title: 'Regalo MUNDOXYZ',
      description: 'Reclama tu regalo especial en MUNDOXYZ.',
    };
  }
  if (pathname.startsWith('/market/lugar/')) {
    return {
      title: 'Lugar en MUNDOXYZ',
      description: 'Descubre este lugar en el mercado de MUNDOXYZ Venezuela.',
    };
  }
  if (pathname.startsWith('/market/franquicia/')) {
    return {
      title: 'Franquicia en MUNDOXYZ',
      description: 'Conoce esta franquicia disponible en el mercado de MUNDOXYZ Venezuela.',
    };
  }
  if (pathname.startsWith('/store/') && pathname.split('/').length === 3) {
    return {
      title: 'Tienda en MUNDOXYZ',
      description: 'Explora productos y servicios en esta tienda de MUNDOXYZ Venezuela.',
    };
  }
  if (pathname.startsWith('/store/') && pathname.includes('/invoice/')) {
    return {
      title: 'Factura | MUNDOXYZ',
      description: 'Consulta tu factura en MUNDOXYZ.',
      noindex: true,
    };
  }
  if (pathname.startsWith('/store/') && pathname.includes('/qr')) {
    return {
      title: 'Pago QR | MUNDOXYZ',
      description: 'Realiza pagos rápidos con QR en MUNDOXYZ.',
      noindex: true,
    };
  }
  return null;
}

const SeoManager = () => {
  const { pathname, search } = useLocation();
  const subdomain = useMemo(() => detectCatalogSubdomain(), []);

  // Si estamos en un subdominio de catálogo o en una ruta de catálogo de marca blanca,
  // no sobreescribir con el branding genérico de MundoXYZ (Catalog.js gestiona el branding y favicon)
  const isCatalogRoute = useMemo(() => {
    if (subdomain) return true;
    const clean = pathname.toLowerCase();
    return (
      clean.startsWith('/c/') ||
      clean === '/catalogo' || clean.startsWith('/catalogo/') ||
      clean === '/maho-albi' || clean.startsWith('/maho-albi/') ||
      clean === '/mahoalbi' || clean.startsWith('/mahoalbi/') ||
      clean === '/belenshop' || clean.startsWith('/belenshop/') ||
      clean === '/hotel-aguamarina' || clean.startsWith('/hotel-aguamarina/') ||
      clean === '/aguamarina' || clean.startsWith('/aguamarina/') ||
      clean === '/gamingspot' || clean.startsWith('/gamingspot/')
    );
  }, [subdomain, pathname]);

  const meta = useMemo(() => {
    if (isCatalogRoute) return null;
    const routeMeta = matchRouteMeta(pathname) || {};
    const baseUrl = 'https://mundoxyz.com';
    const canonical = `${baseUrl}${pathname}`;
    const url = `${baseUrl}${pathname}${search}`;
    return {
      ...DEFAULT,
      ...routeMeta,
      canonical,
      url,
    };
  }, [isCatalogRoute, pathname, search]);

  if (isCatalogRoute || !meta) {
    return null;
  }

  return <SeoHelmet {...meta} />;
};

export default SeoManager;
